import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  alpha,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CalendarMonth as CalendarIcon,
  DirectionsCar as VehicleIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  ArrowDownward as EarlyIcon,
  ArrowUpward as LateIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import isoWeek from 'dayjs/plugin/isoWeek';
import { 
  fetchPlannedShifts, 
  cancelPlannedShift,
  deletePlannedShift,
  fetchActualShiftsForComparison,
  clearPlannedShiftsError
} from '../../redux/slices/plannedShiftsSlice';
import {
  getPlannedShiftIntervals,
  matchActualShifts,
  getActualShiftStart,
  getActualShiftEnd
} from '../../utils/plannedShiftTime';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';

dayjs.extend(isoWeek);
dayjs.locale('de');

// Constants
const HOUR_WIDTH = 120;
const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 50;
const ROLLING_HOURS_DISPLAY = 13; // Show 1 past hour + 12 future hours

// Shift definitions for vehicle availability
const SHIFT_DEFINITIONS = {
  '8h': {
    early: { start: 6, end: 14, label: 'Frühschicht' },
    late: { start: 14, end: 22, label: 'Spätschicht' },
    night: { start: 22, end: 6, label: 'Nachtschicht', overnight: true }
  },
  '12h': {
    day: { start: 6, end: 18, label: 'Tagschicht' },
    night: { start: 18, end: 6, label: 'Nachtschicht', overnight: true }
  }
};

const ShiftPlanner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  const { plannedShifts, actualShifts, isLoading, error } = useSelector(state => state.plannedShifts);
  const { drivers } = useSelector(state => state.drivers);
  const { vehicles } = useSelector(state => state.vehicles);
  const { shifts: appDataShifts } = useSelector(state => state.appData); // Get shifts from AppDataManager
  
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [isRollingView, setIsRollingView] = useState(true); // Rolling 24h calendar
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [displayMode, setDisplayMode] = useState('drivers'); // 'drivers' or 'vehicles'
  const [shiftDuration, setShiftDuration] = useState('12h'); // '8h' or '12h'
  
  // Context menu and delete dialog states
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState('single'); // 'single' or 'all'
  const [reloadKey, setReloadKey] = useState(0);
  
  // Check if current date is today
  const isToday = currentDate.isSame(dayjs(), 'day');
  
  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
  }, [dispatch]);

  // Update current time every minute
  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(dayjs());
    };
    
    // Update immediately
    updateTime();
    
    // Set up interval to update every minute
    const interval = setInterval(updateTime, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Fahrer- und Fahrzeugfilter teilen sich `selectedDriver`. Wechselt die
  // Art des Filters, zurueck auf „alle" — sonst wird nach einer Fahrzeug-ID
  // als Fahrer gefiltert (und umgekehrt) und die Liste ist leer.
  const filterType = viewMode === 'week' && displayMode === 'vehicles' ? 'vehicles' : 'drivers';
  useEffect(() => {
    setSelectedDriver('all');
  }, [filterType]);

  // Fetch planned shifts based on view
  useEffect(() => {
    let startDate, endDate;
    
    if (isRollingView && isToday && viewMode === 'day') {
      // Rollierendes Fenster reicht in den Folgetag
      startDate = dayjs().format('YYYY-MM-DD');
      endDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
    } else if (viewMode === 'week') {
      startDate = currentDate.startOf('isoWeek').format('YYYY-MM-DD');
      endDate = currentDate.endOf('isoWeek').format('YYYY-MM-DD');
    } else {
      startDate = currentDate.format('YYYY-MM-DD');
      endDate = startDate;
    }
    // (Der Vortag fuer Nachtschichten wird im Slice mitgeladen.)
    
    const params = { startDate, endDate };
    if (filterType === 'drivers' && selectedDriver !== 'all') {
      params.driverId = selectedDriver;
    }
    
    dispatch(fetchPlannedShifts(params));
    
    // Tatsaechliche App-Schichten fuer alle angezeigten Tage plus Vortag
    // (Nachtschichten). Frueher wurde in der Wochenansicht nur heute geladen.
    const actualDates = new Set();
    const firstDay = dayjs(startDate).subtract(1, 'day');
    const lastDay = dayjs(endDate);
    for (let day = firstDay; !day.isAfter(lastDay, 'day'); day = day.add(1, 'day')) {
      if (day.isAfter(dayjs(), 'day')) break; // Zukunft hat noch keine App-Schichten
      actualDates.add(day.format('YYYY-MM-DD'));
    }
    actualDates.forEach((date) => dispatch(fetchActualShiftsForComparison({ date })));
  }, [dispatch, currentDate, viewMode, selectedDriver, filterType, isRollingView, isToday, reloadKey]);
  
  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };
  
  const handleDateChange = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(prev => direction === 'next' ? prev.add(1, 'week') : prev.subtract(1, 'week'));
    } else {
      setCurrentDate(prev => direction === 'next' ? prev.add(1, 'day') : prev.subtract(1, 'day'));
    }
  };
  
  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleDisplayModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDisplayMode(newMode);
    }
  };


  const handleShiftDurationChange = (event, newDuration) => {
    if (newDuration !== null) {
      setShiftDuration(newDuration);
    }
  };
  
  
  const handleEditShift = (shiftId) => {
    // Don't allow editing of unplanned shifts
    if (shiftId && shiftId.startsWith('unplanned-')) {
      console.log('Ungeplante Schichten können nicht bearbeitet werden');
      return;
    }
    navigate(`/schichtplaner/${shiftId}/bearbeiten`);
  };
  
  
  // Context menu handlers
  const handleContextMenu = (event, shift) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setSelectedShift(shift);
  };
  
  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setSelectedShift(null);
  };
  
  const handleEditFromMenu = () => {
    if (selectedShift) {
      handleEditShift(selectedShift.id);
    }
    handleCloseContextMenu();
  };
  
  const handleDeleteFromMenu = () => {
    if (selectedShift) {
      setDeleteDialogOpen(true);
      // Close context menu but keep selectedShift for the dialog
      setContextMenuAnchor(null);
    } else {
      handleCloseContextMenu();
    }
  };
  
  // Delete dialog handlers
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedShift(null);
    setDeleteOption('single');
  };
  
  const handleConfirmDelete = async () => {
    if (!selectedShift || !selectedShift.id) return;
    
    try {
      if (deleteOption === 'single') {
        // Starttag der Schicht loeschen — nicht den Tag des angeklickten
        // Stuecks (bei Nachtschichten waere das der Folgetag).
        const datesToDelete = [selectedShift.date];
        await dispatch(cancelPlannedShift({ id: selectedShift.id, cancelDates: datesToDelete }));
      } else {
        // Delete all dates (entire shift)
        await dispatch(deletePlannedShift(selectedShift.id));
      }
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  };
  
  // Get the original dates array for a shift
  const getOriginalDates = (shift) => {
    if (!shift) return [];
    
    // Try different sources for dates
    if (shift.originalDates && Array.isArray(shift.originalDates)) {
      return shift.originalDates;
    }
    
    if (shift.dates && Array.isArray(shift.dates)) {
      return shift.dates;
    }
    
    // Find original shift by ID
    if (shift.id && plannedShifts && plannedShifts.length > 0) {
      const originalShift = plannedShifts.find(s => s.id === shift.id);
      if (originalShift && originalShift.dates && Array.isArray(originalShift.dates)) {
        return originalShift.dates;
      }
    }
    
    return [];
  };

  // Check if shift has multiple dates
  const hasMultipleDates = (shift) => {
    const dates = getOriginalDates(shift);
    return dates.length > 1;
  };
  
  // Beginn des sichtbaren Zeitfensters in der Tagesansicht: rollierend eine
  // Stunde vor jetzt (volle Stunde, wie die Zeitleiste), sonst 00:00 des Tages.
  const isRollingDayView = isRollingView && isToday && viewMode === 'day';
  const getDayViewStart = () => (
    isRollingDayView
      ? currentTime.startOf('hour').subtract(1, 'hour')
      : currentDate.startOf('day')
  );
  const getDayViewEnd = () => getDayViewStart().add(isRollingDayView ? ROLLING_HOURS_DISPLAY : 24, 'hour');

  // Liegt ein Stueck (Tagesabschnitt einer Schicht) in der aktuellen Ansicht?
  const isSegmentInView = (segmentStart, segmentEnd, displayDate) => {
    if (viewMode === 'day') {
      if (isRollingDayView) {
        return segmentStart.isBefore(getDayViewEnd()) && segmentEnd.isAfter(getDayViewStart());
      }
      return dayjs(displayDate).isSame(currentDate, 'day');
    }
    return dayjs(displayDate).isSame(currentDate, 'isoWeek');
  };

  // Alle geladenen App-Schichtdaten eines Fahrers (mehrere Tage + Live-Daten)
  const getDriverActualEntries = (driverId) => {
    const entries = Object.values(actualShifts || {})
      .map((driversOfDay) => driversOfDay?.[driverId])
      .filter(Boolean);
    if (appDataShifts?.drivers?.[driverId]) {
      entries.push(appDataShifts.drivers[driverId]);
    }
    return entries;
  };

  // Get shifts for current view
  const getShiftsForDisplay = () => {
    const shiftsMap = new Map();
    // Tatsaechliche Schichten, die schon einer geplanten zugeordnet sind
    // (driverId -> Set der Startzeitpunkte), damit sie nicht zusaetzlich als
    // „ungeplant" erscheinen.
    const matchedActualStarts = new Map();

    plannedShifts.forEach(shift => {
      if (shift.status === 'cancelled') return;

      const driverEntries = getDriverActualEntries(shift.driverId);

      getPlannedShiftIntervals(shift).forEach((interval) => {
        const matches = matchActualShifts(driverEntries, interval.start, interval.end);
        if (matches.length > 0) {
          if (!matchedActualStarts.has(shift.driverId)) matchedActualStarts.set(shift.driverId, new Set());
          matches.forEach((m) => matchedActualStarts.get(shift.driverId).add(getActualShiftStart(m).valueOf()));
        }
        // Nur die zu DIESEM Termin passenden App-Schichten weitergeben —
        // frueher galt jede Schicht des Fahrers von heute fuer alle Tage.
        const actualShift = matches.length > 0
          ? { shifts: matches, currentShiftNumber: matches.length }
          : null;

        // Nachtschichten in zwei Stuecke teilen: bis Mitternacht am Starttag,
        // Rest ab 00:00 am Folgetag. Ergibt sich aus dem Zeitfenster selbst,
        // nicht aus `dateEndMapping` (Altdaten sind dort unvollstaendig).
        const segments = [];
        const dayEnd = interval.start.endOf('day');
        if (interval.end.isAfter(dayEnd)) {
          segments.push({ start: interval.start, end: dayEnd, displayDate: interval.date, isEndDate: false });
          segments.push({
            start: dayEnd.add(1, 'millisecond'),
            end: interval.end,
            displayDate: interval.end.format('YYYY-MM-DD'),
            isEndDate: true
          });
        } else {
          segments.push({ start: interval.start, end: interval.end, displayDate: interval.date, isEndDate: false });
        }

        segments.forEach((segment) => {
          if (!isSegmentInView(segment.start, segment.end, segment.displayDate)) return;
          const key = `${shift.id}-${interval.date}-${segment.isEndDate ? 'end' : 'start'}`;
          shiftsMap.set(key, {
            ...shift,
            date: interval.date, // Starttag der Schicht (fuer Loeschen/Bearbeiten)
            displayDate: segment.displayDate, // Tag, an dem dieses Stueck steht
            startTime: segment.start,
            endTime: segment.end,
            plannedStart: interval.start,
            plannedEnd: interval.end,
            actualShift,
            isEndDateDisplay: segment.isEndDate,
            isOvernightShift: segments.length > 1,
            originalStartTimeString: shift.startTime,
            originalDates: shift.dates
          });
        });
      });
    });

    // Then, process unplanned shifts from actualShifts and appDataShifts
    const processUnplannedShifts = (shiftData, source) => {
      if (!shiftData || !shiftData.drivers) {
        return;
      }
      
      Object.entries(shiftData.drivers).forEach(([driverId, driverShiftData]) => {
        if (!driverShiftData || !driverShiftData.shifts) {
          return;
        }
        
        // Nur Fahrer aus der (ggf. auf den Unternehmer gefilterten) Fahrerliste
        const driver = drivers.find(d => d.id === driverId);
        if (!driver) return;
        
        const assignedVehicle = vehicles.find(v => v.current_driver?.id === driverId);
        
        driverShiftData.shifts.forEach((actualShift) => {
          if (!actualShift.isActive && !actualShift.shiftStart) {
            return;
          }
          
          const shiftStartTime = getActualShiftStart(actualShift);
          if (!shiftStartTime) return;

          // Schon einer geplanten Schicht zugeordnet?
          if (matchedActualStarts.get(driverId)?.has(shiftStartTime.valueOf())) {
            return;
          }
          
          const shiftDate = shiftStartTime.format('YYYY-MM-DD');
          // Laufende Schicht ohne Ende: bis jetzt (mind. 1 h) statt pauschal 8 h
          let shiftEndTime = getActualShiftEnd(actualShift);
          if (!shiftEndTime) {
            const now = dayjs();
            shiftEndTime = now.isAfter(shiftStartTime.add(1, 'hour')) ? now : shiftStartTime.add(1, 'hour');
          }
          
          if (!isSegmentInView(shiftStartTime, shiftEndTime, shiftDate)) {
            return;
          }
          
          // Schluessel ueber den Startzeitpunkt: dieselbe Schicht kommt ueber
          // actualShifts und appDataShifts doppelt herein.
          const unplannedShiftKey = `unplanned-${driverId}-${shiftStartTime.valueOf()}`;
          shiftsMap.set(unplannedShiftKey, {
            id: unplannedShiftKey,
            driverId,
            driverName: driver.user?.name || driver.name || 'Unbekannter Fahrer',
            vehicleId: assignedVehicle?.id || null,
            vehiclePlate: assignedVehicle?.licensePlate || null,
            dates: [shiftDate],
            startTime: shiftStartTime,
            endTime: shiftEndTime,
            plannedStart: shiftStartTime,
            plannedEnd: shiftEndTime,
            originalStartTimeString: shiftStartTime.format('HH:mm'),
            plannedDuration: shiftEndTime.diff(shiftStartTime, 'hour', true),
            status: 'unplanned',
            isOvernightShift: false,
            isEndDateDisplay: false,
            actualShift: { ...driverShiftData, shifts: [actualShift], currentShiftNumber: 1 },
            date: shiftDate,
            displayDate: shiftDate,
            originalDates: [shiftDate],
            notes: 'Ungeplante Schicht',
            source
          });
        });
      });
    };
    
    // actualShifts hat die Form { datum: { driverId: driverData } }
    Object.entries(actualShifts).forEach(([date, shiftData]) => {
      processUnplannedShifts({ drivers: shiftData }, `actualShifts-${date}`);
    });
    
    if (appDataShifts) {
      processUnplannedShifts(appDataShifts, 'appDataShifts');
    }
    
    const shiftsArray = Array.from(shiftsMap.values());
    return shiftsArray.sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
  };

  // „22:00 - 06:00 (+1 Tag)" — immer die ganze geplante Schicht, auch auf dem
  // Folgetag-Stueck einer Nachtschicht.
  const formatPlannedRange = (shift, compact = false) => {
    const start = shift.plannedStart || shift.startTime;
    const end = shift.plannedEnd || shift.endTime;
    const range = `${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    if (!shift.isOvernightShift) return range;
    if (compact) return `${range} ${shift.isEndDateDisplay ? '(-1)' : '(+1)'}`;
    return `${range} ${shift.isEndDateDisplay ? '(-1 Tag)' : '(+1 Tag)'}`;
  };
  
  // Position und Breite in der Tages-Zeitleiste — ueber absolute Zeitpunkte,
  // damit auch Schichten nach Mitternacht im rollierenden Fenster stimmen.
  const calculateShiftStyle = (shift) => {
    const viewStart = getDayViewStart();
    const viewEnd = getDayViewEnd();

    if (!shift.startTime.isBefore(viewEnd) || !shift.endTime.isAfter(viewStart)) {
      return { display: 'none' };
    }

    const visibleStart = shift.startTime.isAfter(viewStart) ? shift.startTime : viewStart;
    const visibleEnd = shift.endTime.isBefore(viewEnd) ? shift.endTime : viewEnd;
    const left = visibleStart.diff(viewStart, 'hour', true) * HOUR_WIDTH;
    const width = visibleEnd.diff(visibleStart, 'hour', true) * HOUR_WIDTH;

    return {
      left: `${left}px`,
      width: `${Math.max(width - 4, 10)}px`,
      top: '2px',
      height: `${ROW_HEIGHT - 4}px`
    };
  };
  
  // Get time slots for header
  const getTimeSlots = () => {
    // Only use rolling view if it's enabled AND we're viewing today
    if (isRollingView && isToday) {
      const currentHour = currentTime.hour();
      
      if (viewMode === 'day') {
        // Rolling 24h view starting from 1 hour before current time
        const startHour = Math.floor(currentHour - 1);
        const slots = [];
        
        for (let i = 0; i < ROLLING_HOURS_DISPLAY; i++) {
          const hour = (startHour + i + 24) % 24;
          const displayHour = startHour + i;
          slots.push({ 
            hour: displayHour, 
            actualHour: hour,
            label: `${hour.toString().padStart(2, '0')}:00`,
            isCurrent: hour === currentHour
          });
        }
        
        return slots;
      } else {
        // Week view: show full 24h for each day
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
          slots.push({ 
            hour, 
            actualHour: hour,
            label: `${hour.toString().padStart(2, '0')}:00`,
            isCurrent: currentDate.isSame(currentTime, 'day') && hour === currentHour
          });
        }
        return slots;
      }
    }
    
    // For fixed view (non-rolling or non-today), show all 24 hours
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      slots.push({ 
        hour, 
        actualHour: hour,
        label: `${hour.toString().padStart(2, '0')}:00`,
        isCurrent: false
      });
    }
    return slots;
  };
  
  // Geplante Schicht, die laut Plan laeuft (oder heute begonnen haette), zu
  // der aber keine App-Schicht passt.
  const isUnstartedShift = (shift, now) => (
    shift.status !== 'unplanned' &&
    !shift.actualShift &&
    shift.plannedStart.isBefore(now) &&
    (shift.plannedEnd.isAfter(now) || shift.plannedStart.isSame(now, 'day'))
  );

  // Group shifts by driver
  const groupShiftsByDriver = () => {
    const shifts = getShiftsForDisplay();
    const driverGroups = new Map();
    
    // Initialize groups for all drivers
    drivers.forEach(driver => {
      if (selectedDriver === 'all' || driver.id === selectedDriver) {
        driverGroups.set(driver.id, {
          driver,
          shifts: [],
          hasUnstartedShift: false
        });
      }
    });
    
    // Add shifts to groups and check for unstarted shifts
    const now = dayjs();
    shifts.forEach(shift => {
      if (driverGroups.has(shift.driverId)) {
        const group = driverGroups.get(shift.driverId);
        group.shifts.push(shift);
        
        if (isUnstartedShift(shift, now)) {
          group.hasUnstartedShift = true;
        }
      }
    });
    
    return Array.from(driverGroups.values());
  };
  
  // Calculate current time marker position
  const getCurrentTimeMarkerStyle = () => {
    if (!isRollingView || !isToday || viewMode !== 'day') return { display: 'none' };
    
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { display: 'none' };
    
    const currentHour = currentTime.hour();
    const currentMinutes = currentTime.minute();
    
    // Find the exact slot that contains the current hour
    const currentSlotIndex = timeSlots.findIndex(slot => slot.actualHour === currentHour);
    if (currentSlotIndex === -1) return { display: 'none' };
    
    // Calculate position: slot index * width + minutes within that slot
    const minuteProgress = currentMinutes / 60; // 0-1 progress within the hour
    const leftPosition = (currentSlotIndex * HOUR_WIDTH) + (minuteProgress * HOUR_WIDTH) + 150; // +150 for driver column
    
    return {
      left: `${leftPosition}px`,
      top: '0px',
      bottom: '0px',
      width: '2px',
      position: 'absolute',
      backgroundColor: '#ff4444',
      zIndex: 1000,
      boxShadow: '0 0 4px rgba(255, 68, 68, 0.5)'
    };
  };

  // Flottenauslastung je Stunde der Tageszeitleiste: belegte Fahrzeuge
  // (Schichten ohne Fahrzeug zaehlen je Fahrer) gegen den Bestand ohne
  // Reservefahrzeuge. Frueher zaehlte nur `status === 'active'` (= frei,
  // belegte Autos fielen raus) und das Datum der Schichten wurde ignoriert.
  const calculateFleetUtilization = () => {
    const timeSlots = getTimeSlots();
    const shifts = getShiftsForDisplay();
    const viewStart = getDayViewStart();
    const totalVehicles = vehicles.filter(v => v.active !== false).length;

    return timeSlots.map((slot, index) => {
      const slotStart = viewStart.add(index, 'hour');
      const slotEnd = slotStart.add(1, 'hour');
      const occupied = new Set();
      shifts.forEach((shift) => {
        if (shift.startTime.isBefore(slotEnd) && shift.endTime.isAfter(slotStart)) {
          occupied.add(shift.vehicleId || `driver-${shift.driverId}`);
        }
      });
      const utilizedVehicles = occupied.size;
      return {
        label: slot.label,
        utilized: utilizedVehicles,
        total: totalVehicles,
        percent: totalVehicles > 0 ? Math.min(100, (utilizedVehicles / totalVehicles) * 100) : 0
      };
    });
  };

  // Get week days for week view
  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf('isoWeek');
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.add(i, 'day');
      days.push({
        date: day,
        dateString: day.format('YYYY-MM-DD'),
        dayName: day.format('dddd'),
        dayShort: day.format('dd'),
        dayNumber: day.format('DD'),
        isToday: day.isSame(dayjs(), 'day')
      });
    }
    
    return days;
  };

  // Group shifts by driver and day for week view
  const getShiftsGroupedByDriverAndDay = () => {
    const shifts = getShiftsForDisplay();
    const weekDays = getWeekDays();
    const driverGroups = new Map();
    
    // Initialize groups for all drivers
    drivers.forEach(driver => {
      if (selectedDriver === 'all' || driver.id === selectedDriver) {
        const driverShifts = new Map();
        weekDays.forEach(day => {
          driverShifts.set(day.dateString, []);
        });
        
        driverGroups.set(driver.id, {
          driver,
          shifts: driverShifts,
          hasUnstartedShift: false
        });
      }
    });
    
    // Group shifts by driver and day
    const now = dayjs();
    shifts.forEach(shift => {
      if (driverGroups.has(shift.driverId)) {
        const group = driverGroups.get(shift.driverId);
        const shiftDateString = dayjs(shift.displayDate || shift.date).format('YYYY-MM-DD');
        
        if (group.shifts.has(shiftDateString)) {
          group.shifts.get(shiftDateString).push(shift);
          
          if (isUnstartedShift(shift, now)) {
            group.hasUnstartedShift = true;
          }
        }
      }
    });
    
    return { driverGroups: Array.from(driverGroups.values()), weekDays };
  };

  // Get shifts for a specific vehicle on a specific date
  const getShiftsForVehicle = (vehicleId, date) => {
    return getShiftsForDisplay().filter(shift => 
      shift.vehicleId === vehicleId && 
      (shift.displayDate === date || shift.date === date) &&
      shift.status !== 'cancelled' &&
      // For vehicle availability calculation, exclude end date displays of overnight shifts
      // to prevent double-counting the same shift on consecutive days
      !shift.isEndDateDisplay
    );
  };

  // Calculate vehicle availability for a specific day
  const calculateVehicleAvailability = (vehicle, date) => {
    const shifts = getShiftsForVehicle(vehicle.id, date);
    const availableSlots = [];
    const currentShiftDefinitions = SHIFT_DEFINITIONS[shiftDuration];
    
    // Sort shifts by start time
    const sortedShifts = shifts.sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
    
    // If no shifts, check availability for each shift type
    if (sortedShifts.length === 0) {
      Object.keys(currentShiftDefinitions).forEach(shiftType => {
        const shiftDef = currentShiftDefinitions[shiftType];
        if (shiftDef) {
          availableSlots.push({
            start: `${shiftDef.start.toString().padStart(2, '0')}:00`,
            end: `${shiftDef.end.toString().padStart(2, '0')}:00`,
            duration: shiftDef.overnight ? (24 - shiftDef.start + shiftDef.end) : (shiftDef.end - shiftDef.start),
            shiftType,
            isLongEnough: (shiftDef.overnight ? (24 - shiftDef.start + shiftDef.end) : (shiftDef.end - shiftDef.start)) >= (shiftDuration === '12h' ? 12 : 8),
            isFree: true
          });
        }
      });
    } else {
      // Calculate gaps between shifts
      let currentTime = 0; // Start of day (00:00)
      
      sortedShifts.forEach((shift, index) => {
        const shiftStart = shift.startTime.hour() + (shift.startTime.minute() / 60);
        const shiftEnd = shift.endTime.hour() + (shift.endTime.minute() / 60);
        
        // Check for gap before this shift
        if (shiftStart > currentTime) {
          const gapDuration = shiftStart - currentTime;
          if (gapDuration >= 1) { // At least 1 hour gap
            // Check which shift types this gap covers
            Object.keys(currentShiftDefinitions).forEach(shiftType => {
              const shiftDef = currentShiftDefinitions[shiftType];
              if (shiftDef) {
                const shiftTypeStart = shiftDef.start;
                const shiftTypeEnd = shiftDef.overnight ? shiftDef.end + 24 : shiftDef.end;
                
                // Check if gap overlaps with shift type
                const overlapStart = Math.max(currentTime, shiftTypeStart);
                const overlapEnd = Math.min(shiftStart, shiftTypeEnd);
                
                if (overlapEnd > overlapStart) {
                  const overlapDuration = overlapEnd - overlapStart;
                  if (overlapDuration >= 1) {
                    availableSlots.push({
                      start: `${Math.floor(overlapStart).toString().padStart(2, '0')}:${Math.round((overlapStart % 1) * 60).toString().padStart(2, '0')}`,
                      end: `${Math.floor(overlapEnd).toString().padStart(2, '0')}:${Math.round((overlapEnd % 1) * 60).toString().padStart(2, '0')}`,
                      duration: overlapDuration,
                      shiftType,
                      isLongEnough: overlapDuration >= (shiftDuration === '12h' ? 12 : 8),
                      isFree: true
                    });
                  }
                }
              }
            });
          }
        }
        
        currentTime = Math.max(currentTime, shiftEnd);
      });
      
      // Check for gap after last shift until end of day
      if (currentTime < 24) {
        const gapDuration = 24 - currentTime;
        if (gapDuration >= 1) {
          Object.keys(currentShiftDefinitions).forEach(shiftType => {
            const shiftDef = currentShiftDefinitions[shiftType];
            if (shiftDef) {
              const shiftTypeStart = shiftDef.start;
              const shiftTypeEnd = shiftDef.overnight ? shiftDef.end + 24 : shiftDef.end;
              
              // Check if gap overlaps with shift type
              const overlapStart = Math.max(currentTime, shiftTypeStart);
              // For overnight shifts, allow the end time to extend beyond 24:00
              const overlapEnd = shiftDef.overnight ? Math.min(shiftTypeEnd, currentTime + (24 - currentTime) + shiftDef.end) : Math.min(24, shiftTypeEnd);
              
              if (overlapEnd > overlapStart) {
                const overlapDuration = overlapEnd - overlapStart;
                if (overlapDuration >= 1) {
                  // Format the end time correctly for overnight shifts
                  let endHour = Math.floor(overlapEnd);
                  let endTimeStr;
                  if (endHour >= 24) {
                    endHour = endHour - 24;
                    endTimeStr = `${endHour.toString().padStart(2, '0')}:${Math.round((overlapEnd % 1) * 60).toString().padStart(2, '0')}`;
                  } else {
                    endTimeStr = `${endHour.toString().padStart(2, '0')}:${Math.round((overlapEnd % 1) * 60).toString().padStart(2, '0')}`;
                  }
                  
                  availableSlots.push({
                    start: `${Math.floor(overlapStart).toString().padStart(2, '0')}:${Math.round((overlapStart % 1) * 60).toString().padStart(2, '0')}`,
                    end: endTimeStr,
                    duration: overlapDuration,
                    shiftType,
                    isLongEnough: overlapDuration >= (shiftDuration === '12h' ? 12 : 8),
                    isFree: true,
                    isOvernight: shiftDef.overnight && overlapEnd > 24
                  });
                }
              }
            }
          });
        }
      }
    }
    
    // Remove duplicates and merge overlapping slots
    const uniqueSlots = [];
    availableSlots.forEach(slot => {
      const existing = uniqueSlots.find(s => 
        s.shiftType === slot.shiftType && 
        Math.abs(parseFloat(s.start.replace(':', '.')) - parseFloat(slot.start.replace(':', '.'))) < 0.1
      );
      if (!existing) {
        uniqueSlots.push(slot);
      }
    });
    
    return {
      vehicleId: vehicle.id,
      date,
      availableSlots: uniqueSlots,
      totalFreeHours: uniqueSlots.reduce((sum, slot) => sum + slot.duration, 0),
      longestFreeSlot: Math.max(...uniqueSlots.map(slot => slot.duration), 0)
    };
  };

  // Group vehicles by day for week view
  const getVehiclesGroupedByDay = () => {
    const weekDays = getWeekDays();
    const vehicleGroups = new Map();
    
    // Initialize groups for all vehicles
    // Reservefahrzeuge (active === false) sind nicht verplanbar
    vehicles.filter(vehicle => vehicle.active !== false).forEach(vehicle => {
      if (selectedDriver === 'all' || vehicle.id === selectedDriver) {
        const vehicleAvailability = new Map();
        weekDays.forEach(day => {
          const availability = calculateVehicleAvailability(vehicle, day.dateString);
          vehicleAvailability.set(day.dateString, availability);
        });
        
        vehicleGroups.set(vehicle.id, {
          vehicle,
          availability: vehicleAvailability
        });
      }
    });
    
    return { vehicleGroups: Array.from(vehicleGroups.values()), weekDays };
  };

  // Color definitions for vehicle availability
  const getAvailabilityColor = (slot) => {
    if (slot.duration >= 8) {
      return {
        bg: alpha(theme.palette.success.main, 0.2),
        border: theme.palette.success.main,
        text: theme.palette.success.main
      };
    } else if (slot.duration >= 4) {
      return {
        bg: alpha(theme.palette.warning.main, 0.2),
        border: theme.palette.warning.main,
        text: theme.palette.warning.main
      };
    } else {
      return {
        bg: alpha(theme.palette.error.main, 0.2),
        border: theme.palette.error.main,
        text: theme.palette.error.main
      };
    }
  };

  // Render vehicle availability for week view
  const renderVehicleAvailability = (vehicleGroup, day) => {
    const dayAvailability = vehicleGroup.availability.get(day.dateString);

    // Vergangene Tage nicht als „frei" anbieten
    if (day.date.isBefore(dayjs(), 'day')) {
      return (
        <Box sx={{ p: 0.5, textAlign: 'center', minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.6rem' }}>
            –
          </Typography>
        </Box>
      );
    }
    
    if (!dayAvailability || dayAvailability.availableSlots.length === 0) {
      return (
        <Box sx={{ 
          p: 0.5, 
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.grey[500], 0.1),
          borderRadius: 1,
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="caption" sx={{ 
            color: theme.palette.text.secondary,
            fontSize: '0.6rem'
          }}>
            Nicht verfügbar
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {dayAvailability.availableSlots.map((slot, index) => {
          const colors = getAvailabilityColor(slot);
          return (
            <Box
              key={index}
              sx={{
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: 1,
                padding: '2px 4px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(colors.border, 0.3)
                }
              }}
            >
              <Typography variant="caption" sx={{ 
                fontWeight: 'bold',
                fontSize: '0.65rem',
                display: 'block',
                color: colors.text,
                lineHeight: 1.1
              }}>
                {slot.start} - {slot.end}
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.55rem',
                color: theme.palette.text.secondary,
                display: 'block',
                lineHeight: 1
              }}>
                {Math.round(slot.duration)}h frei
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.5rem',
                color: theme.palette.text.secondary,
                display: 'block',
                lineHeight: 1
              }}>
                {SHIFT_DEFINITIONS[shiftDuration][slot.shiftType]?.label || slot.shiftType}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Render shift for week view (compact format)
  const renderShiftForWeek = (shift) => {
    const isActive = shift.actualShift !== null;
    const hasConflict = isActive && shift.actualShift.currentShiftNumber > 1;
    const isUnplanned = shift.status === 'unplanned';
    
    // Check if shift should have started but hasn't
    const now = dayjs();
    const isPlannedButNotStarted = !isActive && shift.plannedStart.isBefore(now) && !isUnplanned;
    
    // Extract actual shift times if available
    let actualStartTime = null;
    let actualEndTime = null;
    let isActuallyStarted = false;
    let isActuallyEnded = false;
    let startedEarly = false;
    let startedLate = false;
    let endedEarly = false;
    let endedLate = false;
    let startTimeDiff = 0;
    let endTimeDiff = 0;
    
    if (isActive && shift.actualShift) {
      let activeShift = shift.actualShift.shifts?.find(s => s.isActive);
      
      if (!activeShift && shift.actualShift.shifts && shift.actualShift.shifts.length > 0) {
        activeShift = shift.actualShift.shifts[shift.actualShift.shifts.length - 1];
      }
      
      if (activeShift) {
        if (activeShift.startTime) {
          actualStartTime = dayjs(activeShift.startTime);
          isActuallyStarted = true;
        } else if (activeShift.shiftStart) {
          const timestamp = activeShift.shiftStart;
          if (timestamp && timestamp.seconds) {
            actualStartTime = dayjs.unix(timestamp.seconds);
            isActuallyStarted = true;
          }
        }
        
        if (actualStartTime) {
          startTimeDiff = actualStartTime.diff(shift.plannedStart, 'minute');
          startedEarly = startTimeDiff < -5;
          startedLate = startTimeDiff > 5;
        }
        
        if (activeShift.endTime) {
          actualEndTime = dayjs(activeShift.endTime);
          isActuallyEnded = true;
        } else if (activeShift.shiftEnd && activeShift.shiftEnd.seconds) {
          actualEndTime = dayjs.unix(activeShift.shiftEnd.seconds);
          isActuallyEnded = true;
        }
        
        if (actualEndTime && isActuallyEnded) {
          endTimeDiff = actualEndTime.diff(shift.plannedEnd, 'minute');
          endedEarly = endTimeDiff < -5;
          endedLate = endTimeDiff > 5;
        }
      }
    }
    
    // Find vehicle information
    const vehicle = shift.vehicleId ? vehicles.find(v => v.id === shift.vehicleId) : null;
    
    return (
      <Box
        key={`${shift.id}-${shift.date}`}
        sx={{
          backgroundColor: shift.isEndDateDisplay 
            ? alpha(theme.palette.grey[400], 0.2) // Gray background for end date displays
            : isUnplanned ? alpha(theme.palette.warning.main, 0.2) : isActive ? alpha(theme.palette.success.main, 0.2) : isPlannedButNotStarted ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.primary.main, 0.2),
          border: `1px solid ${shift.isEndDateDisplay 
            ? theme.palette.grey[400] // Gray border for end date displays
            : isUnplanned ? theme.palette.warning.main : isActive ? theme.palette.success.main : isPlannedButNotStarted ? theme.palette.error.main : theme.palette.primary.main}`,
          borderRadius: 1,
          padding: '2px 4px',
          cursor: shift.isEndDateDisplay ? 'default' : 'pointer', // No pointer cursor for end date displays
          mb: 0.25,
          position: 'relative',
          opacity: shift.isEndDateDisplay ? 0.7 : 1, // Slightly transparent for end date displays
          '&:hover': shift.isEndDateDisplay ? {} : { // No hover effect for end date displays
            backgroundColor: isUnplanned ? alpha(theme.palette.warning.main, 0.3) : isActive ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.primary.main, 0.3),
            zIndex: 10
          }
        }}
        onClick={shift.isEndDateDisplay ? undefined : () => handleEditShift(shift.id)} // No click handler for end date displays
      >
        {/* Context menu button - only show for clickable shifts */}
        {!shift.isEndDateDisplay && !isUnplanned && (
          <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 20 }}>
            <IconButton
              size="small"
              onClick={(e) => handleContextMenu(e, shift)}
              sx={{ 
                width: 14, 
                height: 14, 
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': { backgroundColor: alpha(theme.palette.background.paper, 0.9) }
              }}
            >
              <MoreVertIcon sx={{ fontSize: 10 }} />
            </IconButton>
          </Box>
        )}
        
        <Box sx={{ pr: 1.5 }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 'bold',
            color: isActuallyStarted ? theme.palette.text.secondary : 'inherit',
            textDecoration: isActuallyStarted ? 'line-through' : 'none',
            display: 'block',
            fontSize: '0.7rem',
            lineHeight: 1.2
          }}>
            {formatPlannedRange(shift, true)}
            {isUnplanned && ' (ungeplant)'}
          </Typography>
          
          {vehicle && (
            <Box sx={{ mt: 0.25 }}>
              <Typography variant="caption" sx={{ 
                fontSize: '0.55rem', 
                color: theme.palette.text.secondary,
                lineHeight: 1,
                display: 'block'
              }}>
                {vehicle.model || 'Unbekanntes Modell'}
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.55rem', 
                color: theme.palette.text.secondary,
                lineHeight: 1,
                display: 'block'
              }}>
                {vehicle.licensePlate || 'Unbekannt'}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
            {hasConflict && (
              <Tooltip title="Mehrere Schichten am Tag">
                <WarningIcon sx={{ fontSize: 8, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {hasMultipleDates(shift) && (
              <Tooltip title={`Mehrtägige Schicht (${shift.dates.length} Tage)`}>
                <Chip 
                  label={shift.dates.length}
                  size="small"
                  sx={{ 
                    fontSize: '0.4rem', 
                    height: 10,
                    backgroundColor: alpha(theme.palette.info.main, 0.2),
                    color: theme.palette.info.main,
                    '& .MuiChip-label': {
                      px: 0.2
                    }
                  }}
                />
              </Tooltip>
            )}
            {startedEarly && (
              <Tooltip title={`${Math.abs(startTimeDiff)} Min. früher`}>
                <EarlyIcon sx={{ fontSize: 8, color: theme.palette.info.main }} />
              </Tooltip>
            )}
            {startedLate && (
              <Tooltip title={`${startTimeDiff} Min. später`}>
                <LateIcon sx={{ fontSize: 8, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {endedEarly && (
              <Tooltip title={`${Math.abs(endTimeDiff)} Min. früher beendet`}>
                <EarlyIcon sx={{ fontSize: 8, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {endedLate && (
              <Tooltip title={`${endTimeDiff} Min. später beendet`}>
                <LateIcon sx={{ fontSize: 8, color: theme.palette.error.main }} />
              </Tooltip>
            )}
          </Box>
          
          {isActuallyStarted && (
            <Typography variant="caption" sx={{ 
              color: theme.palette.success.main,
              fontWeight: 'bold',
              fontSize: '0.6rem',
              display: 'block',
              lineHeight: 1
            }}>
              {actualStartTime.format('HH:mm')} - {actualEndTime ? actualEndTime.format('HH:mm') : 'läuft'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };
  
  const renderShift = (shift) => {
    const isActive = shift.actualShift !== null;
    const hasConflict = isActive && shift.actualShift.currentShiftNumber > 1;
    const isUnplanned = shift.status === 'unplanned';
    
    // Check if shift should have started but hasn't
    const now = dayjs();
    const isPlannedButNotStarted = !isActive && shift.plannedStart.isBefore(now) && !isUnplanned;
    
    // Extract actual shift times if available
    let actualStartTime = null;
    let actualEndTime = null;
    let isActuallyStarted = false;
    let isActuallyEnded = false;
    let startedEarly = false;
    let startedLate = false;
    let endedEarly = false;
    let endedLate = false;
    let startTimeDiff = 0;
    let endTimeDiff = 0;
    
    if (isActive && shift.actualShift) {
      // Try to find active shift in the shifts array
      let activeShift = shift.actualShift.shifts?.find(s => s.isActive);
      
      // If no active shift found, look for the most recent shift (ended shifts)
      if (!activeShift && shift.actualShift.shifts && shift.actualShift.shifts.length > 0) {
        // Get the last shift (most recent) regardless of isActive status
        activeShift = shift.actualShift.shifts[shift.actualShift.shifts.length - 1];
      }
      
      if (activeShift) {
        // Handle both possible timestamp formats
        if (activeShift.startTime) {
          actualStartTime = dayjs(activeShift.startTime);
          isActuallyStarted = true;
        } else if (activeShift.shiftStart) {
          // Convert Firebase Timestamp to dayjs
          const timestamp = activeShift.shiftStart;
          if (timestamp && timestamp.seconds) {
            actualStartTime = dayjs.unix(timestamp.seconds);
            isActuallyStarted = true;
          }
        }
        
        if (actualStartTime) {
          // Compare actual vs planned start time
          startTimeDiff = actualStartTime.diff(shift.plannedStart, 'minute');
          startedEarly = startTimeDiff < -5; // Started more than 5 minutes early
          startedLate = startTimeDiff > 5; // Started more than 5 minutes late
        }
        
        // Handle endTime/shiftEnd
        if (activeShift.endTime) {
          actualEndTime = dayjs(activeShift.endTime);
          isActuallyEnded = true;
        } else if (activeShift.shiftEnd && activeShift.shiftEnd.seconds) {
          actualEndTime = dayjs.unix(activeShift.shiftEnd.seconds);
          isActuallyEnded = true;
        }
        
        // Compare actual vs planned end time
        if (actualEndTime && isActuallyEnded) {
          endTimeDiff = actualEndTime.diff(shift.plannedEnd, 'minute');
          endedEarly = endTimeDiff < -5; // Ended more than 5 minutes early
          endedLate = endTimeDiff > 5; // Ended more than 5 minutes late
          
        }
      } else {
        // Fallback: check if actualShift has direct properties
        if (shift.actualShift.startTime) {
          actualStartTime = dayjs(shift.actualShift.startTime);
          isActuallyStarted = true;
          
          const timeDiff = actualStartTime.diff(shift.plannedStart, 'minute');
          startedEarly = timeDiff < -5;
          startedLate = timeDiff > 5;
        }
        if (shift.actualShift.endTime) {
          actualEndTime = dayjs(shift.actualShift.endTime);
          isActuallyEnded = true;
        }
      }
    }
    
    // Find vehicle information
    const vehicle = shift.vehicleId ? vehicles.find(v => v.id === shift.vehicleId) : null;
    const vehicleInfo = vehicle ? `${vehicle.licensePlate || 'Unbekannt'} - ${vehicle.model || 'Unbekanntes Modell'}` : null;
    
    return (
      <Box
        key={`${shift.id}-${shift.date}`}
        sx={{
          position: 'absolute',
          ...calculateShiftStyle(shift, isPlannedButNotStarted),
          backgroundColor: shift.isEndDateDisplay 
            ? alpha(theme.palette.grey[400], 0.2) // Gray background for end date displays
            : isUnplanned ? alpha(theme.palette.warning.main, 0.2) : isActive ? alpha(theme.palette.success.main, 0.2) : isPlannedButNotStarted ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.primary.main, 0.2),
          border: `2px solid ${shift.isEndDateDisplay 
            ? theme.palette.grey[400] // Gray border for end date displays
            : isUnplanned ? theme.palette.warning.main : isActive ? theme.palette.success.main : isPlannedButNotStarted ? theme.palette.error.main : theme.palette.primary.main}`,
          borderRadius: 1,
          padding: '4px 8px',
          cursor: shift.isEndDateDisplay ? 'default' : 'pointer', // No pointer cursor for end date displays
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'hidden',
          opacity: shift.isEndDateDisplay ? 0.7 : 1, // Slightly transparent for end date displays
          '&:hover': shift.isEndDateDisplay ? {} : { // No hover effect for end date displays
            backgroundColor: isUnplanned ? alpha(theme.palette.warning.main, 0.3) : isActive ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.primary.main, 0.3),
            zIndex: 10
          }
        }}
        onClick={shift.isEndDateDisplay ? undefined : () => handleEditShift(shift.id)} // No click handler for end date displays
      >
        {/* Context menu button - only show for clickable shifts */}
        {!shift.isEndDateDisplay && !isUnplanned && (
          <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 20 }}>
            <IconButton
              size="small"
              onClick={(e) => handleContextMenu(e, shift)}
              sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': { backgroundColor: alpha(theme.palette.background.paper, 0.9) }
              }}
            >
              <MoreVertIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0, width: '100%', pr: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography variant="caption" noWrap sx={{ 
                color: isActuallyStarted ? theme.palette.text.secondary : 'inherit',
                textDecoration: isActuallyStarted ? 'line-through' : 'none'
              }}>
                Geplant: {formatPlannedRange(shift)}
                {isUnplanned && ' (ungeplant)'}
              </Typography>
              {vehicleInfo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                  <VehicleIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" noWrap sx={{ 
                    fontSize: '0.6rem', 
                    color: theme.palette.text.secondary,
                    maxWidth: '180px'
                  }}>
                    {vehicleInfo}
                  </Typography>
                </Box>
              )}
            </Box>
            {isActuallyStarted && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" noWrap sx={{ 
                    color: theme.palette.success.main,
                    fontWeight: 'bold'
                  }}>
                    Start: {actualStartTime.format('HH:mm')}
                  </Typography>
                  {startedEarly && (
                    <Tooltip title={`Schicht ${Math.abs(startTimeDiff)} Minuten früher begonnen`}>
                      <EarlyIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.info.main,
                        transform: 'rotate(45deg)'
                      }} />
                    </Tooltip>
                  )}
                  {startedLate && (
                    <Tooltip title={`Schicht ${startTimeDiff} Minuten später begonnen`}>
                      <LateIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.warning.main,
                        transform: 'rotate(45deg)'
                      }} />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" noWrap sx={{ 
                    color: isActuallyEnded 
                      ? (endedEarly ? theme.palette.warning.main : endedLate ? theme.palette.error.main : theme.palette.success.main)
                      : theme.palette.text.secondary,
                    fontWeight: 'bold'
                  }}>
                    Ende: {actualEndTime ? actualEndTime.format('HH:mm') : 'läuft'}
                  </Typography>
                  {endedEarly && (
                    <Tooltip title={`Schicht ${Math.abs(endTimeDiff)} Minuten früher beendet`}>
                      <EarlyIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.warning.main,
                        transform: 'rotate(-45deg)'
                      }} />
                    </Tooltip>
                  )}
                  {endedLate && (
                    <Tooltip title={`Schicht ${endTimeDiff} Minuten später beendet`}>
                      <LateIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.error.main,
                        transform: 'rotate(-45deg)'
                      }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {hasConflict && (
              <Tooltip title="Mehrere Schichten am Tag">
                <WarningIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {hasMultipleDates(shift) && (
              <Tooltip title={`Mehrtägige Schicht (${shift.dates.length} Tage)`}>
                <Chip 
                  label={shift.dates.length}
                  size="small"
                  sx={{ 
                    fontSize: '0.6rem', 
                    height: 16,
                    backgroundColor: alpha(theme.palette.info.main, 0.2),
                    color: theme.palette.info.main,
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              </Tooltip>
            )}
            {viewMode === 'week' && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                {dayjs(shift.date).format('dd')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };
  
  const renderFleetUtilization = () => {
    const utilization = calculateFleetUtilization();
    
    return (
      <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Flottenauslastung
        </Typography>
        <Box sx={{ position: 'relative', height: 40, mt: 1 }}>
          {utilization.map((slot, index) => (
            <Tooltip
              key={`${slot.label}-${index}`}
              title={`${slot.label}: ${slot.utilized}/${slot.total} Fahrzeuge (${Math.round(slot.percent)}%)`}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: `${(index * 100) / utilization.length}%`,
                  width: `${100 / utilization.length}%`,
                  borderRight: `1px solid ${theme.palette.background.paper}`,
                  height: '100%',
                  backgroundColor: 
                    slot.percent >= 80 ? theme.palette.error.main :
                    slot.percent >= 60 ? theme.palette.warning.main :
                    slot.percent >= 40 ? theme.palette.success.main :
                    theme.palette.surface[300],
                  opacity: 0.6,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            </Tooltip>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Unterbesetzt (&lt;40%)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vollbesetzt (&gt;80%)
          </Typography>
        </Box>
      </Paper>
    );
  };
  
  // Vollbild-Laden nur beim allerersten Laden. Danach bleibt der Plan stehen
  // (sonst springt die Seite bei jedem Tageswechsel/Loeschen), Fehler kommen
  // als Hinweis ueber dem Plan statt ihn zu ersetzen.
  if (isLoading && plannedShifts.length === 0 && drivers.length === 0) {
    return <Loading message="Schichtplan wird geladen..." />;
  }

  const handleRetry = () => {
    dispatch(clearPlannedShiftsError());
    setReloadKey((key) => key + 1);
  };
  
  const driverGroups = groupShiftsByDriver();
  const timeSlots = getTimeSlots();
  
  // Get data for week view
  const { driverGroups: weekDriverGroups, weekDays } = getShiftsGroupedByDriverAndDay();
  const { vehicleGroups: weekVehicleGroups } = displayMode === 'vehicles' ? getVehiclesGroupedByDay() : { vehicleGroups: [] };
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Schichtplaner"
        description="Planung und Übersicht aller geplanten Schichten"
        actionButton={{
          label: 'Schicht hinzufügen',
          icon: <AddIcon />,
          path: '/schichtplaner/neu'
        }}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichtplaner', path: '/schichtplaner' }
        ]}
      />
      
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={handleRetry}>Erneut laden</Button>}
        >
          {error}
        </Alert>
      )}

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              fullWidth
            >
              <ToggleButton value="day">
                <TodayIcon sx={{ mr: 1 }} />
                Tag
              </ToggleButton>
              <ToggleButton value="week">
                <WeekIcon sx={{ mr: 1 }} />
                Woche
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          
          {viewMode === 'week' && (
            <Grid size={{ xs: 12, md: 2 }}>
              <ToggleButtonGroup
                value={displayMode}
                exclusive
                onChange={handleDisplayModeChange}
                size="small"
                fullWidth
              >
                <ToggleButton value="drivers">
                  <PersonIcon sx={{ mr: 1 }} />
                  Fahrer
                </ToggleButton>
                <ToggleButton value="vehicles">
                  <VehicleIcon sx={{ mr: 1 }} />
                  Fahrzeuge
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}
          
          <Grid size={{ xs: 12, md: displayMode === 'vehicles' && viewMode === 'week' ? 2.5 : 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{viewMode === 'week' && displayMode === 'vehicles' ? 'Fahrzeuge' : 'Fahrer'}</InputLabel>
              <Select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                label={viewMode === 'week' && displayMode === 'vehicles' ? 'Fahrzeuge' : 'Fahrer'}
              >
                <MenuItem value="all">{viewMode === 'week' && displayMode === 'vehicles' ? 'Alle Fahrzeuge' : 'Alle Fahrer'}</MenuItem>
                {viewMode === 'week' && displayMode === 'vehicles' ? (
                  vehicles && vehicles.length > 0 ? (
                    vehicles.map(vehicle => (
                      <MenuItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.licensePlate || 'Unbekannt'} - {vehicle.model || 'Unbekanntes Modell'}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Keine Fahrzeuge verfügbar</MenuItem>
                  )
                ) : (
                  drivers && drivers.length > 0 ? (
                    drivers.map(driver => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.user?.name || driver.name || 'Unbekannter Fahrer'}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Keine Fahrer verfügbar</MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
          
          {displayMode === 'vehicles' && viewMode === 'week' && (
            <>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <ToggleButtonGroup
                  value={shiftDuration}
                  exclusive
                  onChange={handleShiftDurationChange}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="8h">
                    8h
                  </ToggleButton>
                  <ToggleButton value="12h">
                    12h
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </>
          )}
          
          <Grid size={{ xs: 12, md: displayMode === 'vehicles' && viewMode === 'week' ? 4 : viewMode === 'week' ? 5 : 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <IconButton onClick={() => handleDateChange('prev')}>
                <PrevIcon />
              </IconButton>
              
              <Chip
                icon={<CalendarIcon />}
                label={
                  viewMode === 'week'
                    ? `KW ${currentDate.isoWeek()} (${currentDate.startOf('isoWeek').format('DD.MM.')}-${currentDate.endOf('isoWeek').format('DD.MM.')})`
                    : currentDate.format('dddd, DD.MM.YYYY')
                }
                onClick={handleToday}
              />
              
              <IconButton onClick={() => handleDateChange('next')}>
                <NextIcon />
              </IconButton>
              
              {isToday && viewMode === 'day' && (
                <Chip
                  icon={<TimeIcon />}
                  label={isRollingView ? '24h Rolling' : 'Feste Zeiten'}
                  variant={isRollingView ? 'filled' : 'outlined'}
                  onClick={() => setIsRollingView(!isRollingView)}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Nachladen (Tageswechsel, Loeschen) ohne den Plan auszublenden */}
      {isLoading && <LinearProgress sx={{ mb: 1 }} />}
      
      {/* Shift Table */}
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        {viewMode === 'week' ? (
          /* Week View - Day Columns */
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ 
              minWidth: 180 + (weekDays.length * 180), // Driver column + day columns
              width: 'max-content'
            }}>
              {/* Day Headers */}
              <Box sx={{ 
                display: 'flex',
                height: HEADER_HEIGHT,
                borderBottom: `2px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 100
              }}>
                {/* Driver/Vehicle Column Header */}
                <Box sx={{
                  width: 180,
                  flexShrink: 0,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2
                }}>
                  <Typography variant="subtitle2">
                    {displayMode === 'vehicles' ? 'Fahrzeuge' : 'Fahrer'}
                  </Typography>
                </Box>
                
                {/* Day Column Headers */}
                {weekDays.map((day) => (
                  <Box
                    key={day.dateString}
                    sx={{
                      width: 180,
                      minWidth: 180,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: day.isToday ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      position: 'relative',
                      px: 1
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: day.isToday ? 'bold' : 'normal',
                        color: 'inherit'
                      }}
                    >
                      {day.dayName}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.7rem',
                        color: day.isToday ? theme.palette.text.primary : theme.palette.text.secondary
                      }}
                    >
                      {day.dayNumber}
                    </Typography>
                    {day.isToday && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          backgroundColor: theme.palette.primary.main
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
              
              {/* Driver/Vehicle Rows */}
              {displayMode === 'vehicles' ? (
                /* Vehicle Rows */
                weekVehicleGroups.map((group, groupIndex) => (
                  <Box
                    key={group.vehicle.id}
                    sx={{
                      display: 'flex',
                      minHeight: ROW_HEIGHT,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: groupIndex % 2 === 1 ? (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : theme.palette.action.hover) : 'transparent'
                    }}
                  >
                    {/* Vehicle Info Column */}
                    <Box sx={{
                      width: 180,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {group.vehicle.licensePlate || 'Unbekannt'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.vehicle.model || 'Unbekanntes Modell'}
                          </Typography>
                        </Box>
                        <VehicleIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                      </Box>
                    </Box>
                    
                    {/* Day Columns */}
                    {weekDays.map((day) => (
                      <Box
                        key={`${group.vehicle.id}-${day.dateString}`}
                        sx={{
                          width: 180,
                          minWidth: 180,
                          flexShrink: 0,
                          borderRight: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {renderVehicleAvailability(group, day)}
                      </Box>
                    ))}
                  </Box>
                ))
              ) : (
                /* Driver Rows */
                weekDriverGroups.map((group, groupIndex) => (
                  <Box
                    key={group.driver.id}
                    sx={{
                      display: 'flex',
                      minHeight: ROW_HEIGHT,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: groupIndex % 2 === 1 ? (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : theme.palette.action.hover) : 'transparent'
                    }}
                  >
                    {/* Driver Info Column */}
                    <Box sx={{
                      width: 180,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {group.driver.user?.name || group.driver.name || 'Unbekannter Fahrer'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.driver.driverNumber ? `Nr. ${group.driver.driverNumber}` : 'Fahrer'}
                          </Typography>
                        </Box>
                        {group.hasUnstartedShift && (
                          <Tooltip title="Schicht nicht angetreten">
                            <WarningIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Day Columns */}
                    {weekDays.map((day) => (
                      <Box
                        key={`${group.driver.id}-${day.dateString}`}
                        sx={{
                          width: 180,
                          minWidth: 180,
                          flexShrink: 0,
                          borderRight: `1px solid ${theme.palette.divider}`,
                          p: 0.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.25
                        }}
                      >
                        {group.shifts.get(day.dateString)?.map(shift => renderShiftForWeek(shift)) || []}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
              
              {/* Empty state */}
              {(displayMode === 'vehicles' ? weekVehicleGroups.length === 0 : weekDriverGroups.length === 0) && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {displayMode === 'vehicles' 
                      ? 'Keine Fahrzeuge für den ausgewählten Zeitraum gefunden'
                      : 'Keine Fahrer oder Schichten für den ausgewählten Zeitraum gefunden'
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          /* Day View - Timeline */
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ 
              width: timeSlots.length * HOUR_WIDTH + 150, 
              minWidth: timeSlots.length * HOUR_WIDTH + 150,
              position: 'relative' 
            }}>
              {/* Background stripes for better row distinction */}
              {driverGroups.map((group, groupIndex) => (
                groupIndex % 2 === 1 && (
                  <Box
                    key={`stripe-${group.driver.id}`}
                    sx={{
                      position: 'absolute',
                      top: HEADER_HEIGHT + (groupIndex * ROW_HEIGHT),
                      left: 0,
                      right: 0,
                      width: '100%',
                      height: ROW_HEIGHT,
                      backgroundColor: (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : theme.palette.action.hover),
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                )
              ))}
              {/* Current Time Marker */}
              <Box sx={getCurrentTimeMarkerStyle()} />
              {/* Time Header */}
              <Box
                sx={{
                  display: 'flex',
                  height: HEADER_HEIGHT,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  position: 'sticky',
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 100
                }}
              >
                <Box
                  sx={{
                    width: 150,
                    flexShrink: 0,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2
                  }}
                >
                  <Typography variant="subtitle2">Fahrer</Typography>
                </Box>
                {timeSlots.map((slot, index) => (
                  <Box
                    key={`${slot.hour}-${index}`}
                    sx={{
                      width: HOUR_WIDTH,
                      minWidth: HOUR_WIDTH,
                      maxWidth: HOUR_WIDTH,
                      flexShrink: 0,
                      borderRight: index < timeSlots.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: slot.isCurrent ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      position: 'relative'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: slot.isCurrent ? 'bold' : 'normal',
                        color: 'inherit'
                      }}
                    >
                      {slot.label}
                    </Typography>
                    {slot.isCurrent && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          backgroundColor: theme.palette.primary.main
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
              
              {/* Driver Rows */}
              {driverGroups.map((group) => (
                <Box
                  key={group.driver.id}
                  sx={{
                    display: 'flex',
                    height: ROW_HEIGHT,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 150,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {group.driver.user?.name || group.driver.name || 'Unbekannter Fahrer'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.driver.driverNumber ? `Nr. ${group.driver.driverNumber}` : 'Fahrer'}
                        </Typography>
                      </Box>
                      {group.hasUnstartedShift && (
                        <Tooltip title="Schicht nicht angetreten">
                          <WarningIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      flex: 1,
                      width: `${timeSlots.length * HOUR_WIDTH}px`
                    }}
                  >
                    {group.shifts.map(shift => renderShift(shift))}
                  </Box>
                </Box>
              ))}
              
              {/* Empty state */}
              {driverGroups.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Fahrer oder Schichten für den ausgewählten Zeitraum gefunden
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Paper>
      
      {/* Fleet Utilization Timeline */}
      {viewMode === 'day' && renderFleetUtilization()}
      
      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
        slotProps={{ paper: {
          style: {
            maxHeight: 48 * 4.5,
            width: '20ch',
          },
        }}}
      >
        <MenuList>
          <MenuItem onClick={handleEditFromMenu}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bearbeiten</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteFromMenu}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Löschen</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Schicht löschen
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Welche Schichten möchten Sie löschen?
          </Typography>
          
          
          {selectedShift && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Fahrer:</strong> {selectedShift?.driverName || 'Unbekannt'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Zeit:</strong> {selectedShift?.originalStartTimeString || selectedShift?.startTime || 'Unbekannt'} - {selectedShift?.plannedDuration || 0}h
                </Typography>
                {hasMultipleDates(selectedShift) && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tage:</strong> {getOriginalDates(selectedShift).length} Tage ({getOriginalDates(selectedShift).join(', ')})
                  </Typography>
                )}
              </Box>
              
              <RadioGroup
                value={deleteOption}
                onChange={(e) => setDeleteOption(e.target.value)}
                sx={{ mt: 2 }}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        Nur diesen Tag ({selectedShift?.displayDate || selectedShift?.date})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hasMultipleDates(selectedShift) 
                          ? 'Andere Tage der Schicht bleiben bestehen'
                          : 'Nur diesen einzelnen Tag löschen'
                        }
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {hasMultipleDates(selectedShift) 
                          ? `Alle Tage dieser Schicht (${getOriginalDates(selectedShift).length} Tage)`
                          : 'Komplette Schicht'
                        }
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hasMultipleDates(selectedShift)
                          ? 'Die gesamte Schicht wird gelöscht'
                          : 'Die Schicht wird vollständig gelöscht'
                        }
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftPlanner;